// Bulk-selection model hygiene helpers (shared by NewLead's onSelectionModelChange).
//
// Controlled DataGrid selection binds rowSelectionModel to Redux multiLead.leadId.
// For the checkbox count, the "N selected" chip, and the bulk-action object_ids to
// stay EXACTLY equal, the model written to Redux must not carry ids that have no
// visible, actionable checkbox.

const POSITIVE_INTEGER_RE = /^[1-9]\d*$/;
const FLAT_ROW_ID_RE = /^([1-9]\d*)-([1-9]\d*)$/;

function positiveSafeInteger(value) {
    if (typeof value === 'number') {
        return Number.isSafeInteger(value) && value > 0 ? value : null;
    }
    if (typeof value !== 'string' || !POSITIVE_INTEGER_RE.test(value)) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * Return the exact id MUI DataGrid uses for a row. V1 rows have id === lead_id;
 * V2 flat rows have a composite id (leadId-treatmentId) while lead_id remains
 * the canonical numeric lead id.
 */
export function getSelectionRowId(row) {
    return row?.id ?? row?.lead_id;
}

/**
 * Detect the flat/V2 row shape without tying selection behavior to a table name.
 * This keeps the fix additive for V1 tables, including Consultation Attended
 * configurations whose row id is already the lead id.
 */
export function isFlatSelectionForRows(rows = []) {
    return rows.some((row) => (
        row?.id != null
        && row?.lead_id != null
        && String(row.id) !== String(row.lead_id)
    ));
}

/**
 * Resolve one flat DataGrid row id to a positive numeric lead id. The flat API
 * contract is exactly `${leadId}-${treatmentId}`. Both segments must be positive
 * safe integers; invalid ids fail closed so a plausible prefix cannot leak into
 * a lead API.
 */
export function canonicalLeadIdFromFlatRowId(rowId) {
    return flatSelectionTargetFromRowId(rowId)?.leadId ?? null;
}

/**
 * Resolve a flat DataGrid row id into its complete task-mutation identity.
 * The second segment is the LeadTreatment primary key, not a display index.
 * Keeping it here prevents treatment-row actions from widening to every task
 * on the same lead.
 */
export function flatSelectionTargetFromRowId(rowId) {
    const raw = String(rowId ?? '');
    const match = FLAT_ROW_ID_RE.exec(raw);
    if (!match) return null;
    const leadId = positiveSafeInteger(match[1]);
    const leadTreatmentId = positiveSafeInteger(match[2]);
    if (leadId == null || leadTreatmentId == null) return null;
    return { rowId: raw, leadId, leadTreatmentId };
}

/**
 * Return the exact task target represented by every selected row.
 *
 * V1/Booked tables are one row per lead and remain lead-scoped. V2-flat
 * Treatment Started and Consultant Attended tables are one row per treatment,
 * so their targets carry both the lead and LeadTreatment ids. Exact duplicate
 * rows are removed, while sibling treatments on the same lead stay distinct.
 */
export function getSelectedTaskTargetsForApi(multiLead = {}) {
    const selectedRowIds = multiLead?.leadId || [];
    const seen = new Set();
    return selectedRowIds.reduce((targets, rowId) => {
        const target = multiLead?.isFlatSelection
            ? flatSelectionTargetFromRowId(rowId)
            : (() => {
                const leadId = positiveSafeInteger(rowId);
                return leadId == null
                    ? null
                    : { rowId: String(leadId), leadId, leadTreatmentId: null };
            })();
        if (!target || seen.has(target.rowId)) return targets;
        seen.add(target.rowId);
        targets.push(target);
        return targets;
    }, []);
}

/**
 * Single conversion/dedupe boundary for APIs that operate on leads rather than
 * DataGrid rows. Raw Redux/DataGrid ids remain untouched for checked-state, but
 * every API consumer receives deduped positive numeric lead ids. Flat selections
 * additionally require the exact composite row-id grammar above.
 */
export function getSelectedLeadIdsForApi(multiLead = {}) {
    return [...new Set(
        getSelectedTaskTargetsForApi(multiLead).map(({ leadId }) => leadId)
    )];
}

/**
 * A selection belongs to one row-identity mode. If a mounted table changes from
 * flat treatment rows to V1 lead rows (or the reverse), retaining the old model
 * would let invisible rows act on the new dataset. Empty pages are deliberately
 * inconclusive so server-paginated cross-page selections survive paging.
 */
export function selectionMatchesRows(multiLead = {}, rows = []) {
    if (!multiLead?.leadId?.length || !rows.length) return true;
    return Boolean(multiLead.isFlatSelection) === isFlatSelectionForRows(rows);
}

export function isRowSelected(row, selectedRowIds = []) {
    const rowId = getSelectionRowId(row);
    if (rowId == null) return false;
    const selected = new Set(selectedRowIds.map((id) => String(id)));
    return selected.has(String(rowId));
}

/**
 * Remove ids that belong to a row on the CURRENT page which is mid-archive
 * (is_archive_pending) — that row's checkbox is suppressed in the render, so its
 * id in the model would inflate the count/object_ids without a visible tick
 * (bsel-repro's "6 checkboxes for 5 selected" off-by-one).
 *
 * Ids NOT present in `rows` are left untouched: they are legitimate cross-page
 * selections retained by keepNonExistentRowsSelected and must survive paging.
 *
 * @param {(number|string)[]} model  the selection model MUI reported
 * @param {Array<{ lead_id?: number|string, is_archive_pending?: boolean }>} rows  current page rows
 * @returns {(number|string)[]} the cleaned model (same id types preserved)
 */
export function cleanSelectionModel(model = [], rows = [], options = {}) {
    const isFlatSelection = options.isFlatSelection ?? isFlatSelectionForRows(rows);
    const validModel = isFlatSelection
        ? model.filter((id) => canonicalLeadIdFromFlatRowId(id) != null)
        : model;
    const archivePendingOnPage = new Set(
        rows
            .filter((r) => r?.is_archive_pending && getSelectionRowId(r) != null)
            .map((r) => String(getSelectionRowId(r)))
    );
    if (archivePendingOnPage.size === 0) return validModel;
    return validModel.filter((id) => !archivePendingOnPage.has(String(id)));
}

/**
 * Remove the handled lead ids from a selection list. Comparison is String-keyed
 * so a numeric selection (e.g. [42, 43]) prunes correctly against string handled
 * ids (e.g. ['42']). Used by NewLead.handleBulkComplete to prune the Redux
 * selection after a bulk complete — clearing all on full success, or pruning only
 * the completed leads on partial so the leftovers stay selected for a retry.
 * Because the DataGrid selection is controlled by that Redux value, pruning it
 * also unticks the handled rows.
 *
 * @param {(number|string)[]} selectedIds
 * @param {(number|string)[]} handledIds
 * @param {{ isFlatSelection?: boolean }} options
 * @returns {(number|string)[]} the remaining selection (original id types preserved)
 */
export function pruneSelectedLeadIds(selectedIds = [], handledIds = [], options = {}) {
    const handled = new Set(handledIds.map((id) => String(id)));
    return selectedIds.filter((id) => {
        // New task-completion callers pass the exact composite row id. Retain
        // sibling treatments for the same lead when only one row succeeded.
        if (handled.has(String(id))) return false;
        const comparisonId = options.isFlatSelection
            ? canonicalLeadIdFromFlatRowId(id)
            : id;
        // Backwards compatibility for lead-scoped callers: a canonical lead id
        // still removes all of that lead's treatment rows.
        return comparisonId == null || !handled.has(String(comparisonId));
    });
}

/**
 * Build a lead id -> owning practice id map from table rows. In the All
 * Practice Leads table each row carries its own practice (`practiceId`, set
 * from the API `practice` field by useLeadTableHelper). Rows without a
 * practice are skipped so callers fall back to the primary practice.
 *
 * @param {Array<object>} rows
 * @param {Object<string, number|string>} [into] existing map to extend (cross-page selections)
 * @returns {Object<string, number|string>}
 */
export function buildPracticeByLeadId(rows = [], into = {}) {
    (rows || []).forEach((row) => {
        const leadId = positiveSafeInteger(row?.lead_id ?? row?.id)
            ?? canonicalLeadIdFromFlatRowId(row?.id);
        const rawPractice = row?.practiceId ?? row?.practice_id ?? row?.practice?.id ?? row?.practice;
        const practice = positiveSafeInteger(rawPractice);
        if (leadId != null && practice != null) into[leadId] = practice;
    });
    return into;
}
